/**
 * Typed wrapper for the curriculum-templates endpoints (`Capabilities/Assessment/Templates`).
 *
 * Calls the shared `http` axios instance directly rather than going through a generated
 * `api/clients.ts` client: `npm run api` regenerates `APIClient.ts` from the *running* app's OpenAPI
 * document, which this environment has no way to stand up (the app migrates a real SQL Server on
 * startup). Once someone can run `npm run api` against a live server, `CurriculumTemplatesClient` will
 * appear in `APIClient.ts` and this file's three calls can be swapped for it - the response shapes
 * below already match what NSwag would emit (bare payload, camelCase, REST status codes).
 */

import http from "../general/HttpClient";

export interface CurriculumTemplateApplication {
    id: string;
    templateVersion: number;
    appliedAt: string;
    appliedByUserID: string;
}

export interface CurriculumTemplateSummary {
    key: string;
    displayName: string;
    description: string;
    hasSeedData: boolean;
    applications: CurriculumTemplateApplication[];
    /** Present while a wizard for this template is in progress in this organisation. */
    draft?: CurriculumTemplateDraftSummary | null;
}

export interface CurriculumTemplatePreviewItem {
    name: string;
    willCreate: boolean;
}

export interface CurriculumTemplatePreview {
    key: string;
    displayName: string;
    hasSeedData: boolean;
    subjects: CurriculumTemplatePreviewItem[];
    assessmentTypes: CurriculumTemplatePreviewItem[];
    markTypes: CurriculumTemplatePreviewItem[];
    cycles: CurriculumTemplatePreviewItem[];
    /** The subject's divisions and their agreed share of the term mark (weights sum to 1). */
    divisions: CurriculumTemplateDivisionPreview[];
    /** How each division combines its assessments: SimpleAverage (legacy divave) or Aggregate (divaggregate). */
    divisionMethod?: string;
    procedures: CurriculumTemplatePreviewItem[];
    /** Absent for a template that lays down no grade structure. */
    structure?: CurriculumTemplateStructurePreview;
}

export interface CurriculumTemplateDivisionPreview {
    name: string;
    weight: number;
    willCreate: boolean;
}

export interface CurriculumTemplateStructurePreview {
    defaultFacultyName: string;
    defaultSchoolName: string;
    qualificationName: string;
    levels: CurriculumTemplatePreviewItem[];
    existingFaculties: string[];
    existingSchools: string[];
    /** Non-zero means the org already has structure - possibly from the legacy sync, which matches on Source/SourceID. */
    existingQualificationCount: number;
    /** Upper bound: grades x subjects x terms. */
    qualificationModuleCount: number;
    /** Subject-choice rules laid down for each grade, unless a grade already has them. */
    choiceRuleNames?: string[];
}

/** The wizard's choices. Anything left out keeps the template's own value. */
export interface CurriculumTemplateApplyOptions {
    createStructure: boolean;
    assignProcedure: boolean;
    facultyName?: string;
    schoolName?: string;
    levelNames?: string[];
    /** Template subjects the school does not offer. */
    excludedSubjects?: string[];
    /** The school's own subjects, seeded alongside the template's. */
    additionalSubjects?: string[];
    /** The terms, in order. Only month and day are used: terms land in the academic year being applied to. */
    cycles?: CurriculumTemplateCycleOption[];
    /** Division name to its share of the term mark, as fractions summing to 1. */
    divisionWeights?: Record<string, number>;
    /** Template subject-choice rules not to lay down. */
    excludedChoiceRules?: string[];
    /** The assessments laid down in every subject's divisions; empty means none (teachers add their own). */
    assessmentPlan?: CurriculumTemplatePlannedAssessment[];
}

export interface CurriculumTemplatePlannedAssessment {
    division: string;
    name: string;
    assessmentType: string;
    total: number;
    /** Terms by position (1 = the first term); empty means every term. */
    terms: number[];
}

export interface CurriculumTemplateCycleOption {
    name: string;
    /** ISO date (yyyy-mm-dd or a full timestamp). */
    startDate?: string | null;
    endDate?: string | null;
}

/**
 * The wizard's saved progress (or, before anything is saved, the template's defaults). `validationError` says what
 * would stop the choices applying as they stand; `isStale` means the template changed after the draft was started.
 */
export interface CurriculumTemplateDraft {
    templateKey: string;
    templateVersion: number;
    isSaved: boolean;
    isStale: boolean;
    currentStep?: string | null;
    updatedAt?: string | null;
    updatedByUserID?: string | null;
    options: CurriculumTemplateApplyOptions;
    validationError?: string | null;
}

export interface CurriculumTemplateDraftSummary {
    currentStep?: string | null;
    updatedAt: string;
    updatedByUserID: string;
}

export interface CurriculumTemplateStructureResult {
    facultyName: string;
    facultyCreated: boolean;
    schoolName: string;
    schoolCreated: boolean;
    qualificationName: string;
    qualificationCreated: boolean;
    createdLevels: string[];
    skippedLevels: string[];
    createdProgramLevels: number;
    createdQualificationModules: number;
    skippedQualificationModules: number;
    createdDivisionGroups: number;
    createdAssignments: number;
    skippedAssignments: number;
    createdChoiceRules?: number;
    createdAssessments?: number;
}

export interface CurriculumTemplateProcedureResult {
    name: string;
    procedureID: string;
    /** Null when a same-named procedure was found but has never been published - it is then not assigned. */
    procedureVersionID?: string;
    alreadyExisted: boolean;
}

export interface CurriculumTemplateApplyResult {
    key: string;
    templateVersion: number;
    createdSubjects: string[];
    skippedSubjects: string[];
    createdAssessmentTypes: string[];
    skippedAssessmentTypes: string[];
    createdMarkTypes: string[];
    skippedMarkTypes: string[];
    createdCycles: string[];
    skippedCycles: string[];
    createdDivisions: string[];
    skippedDivisions: string[];
    procedures: CurriculumTemplateProcedureResult[];
    structure?: CurriculumTemplateStructureResult;
}

const BASE_URL = "/api/assessment/curriculum-templates";

export const listCurriculumTemplates = (): Promise<CurriculumTemplateSummary[]> =>
    http.get<CurriculumTemplateSummary[]>(BASE_URL).then((r) => r.data);

export const previewCurriculumTemplate = (key: string): Promise<CurriculumTemplatePreview> =>
    http.get<CurriculumTemplatePreview>(`${BASE_URL}/${encodeURIComponent(key)}/preview`).then((r) => r.data);

export const applyCurriculumTemplate = (key: string, options?: CurriculumTemplateApplyOptions): Promise<CurriculumTemplateApplyResult> =>
    http.post<CurriculumTemplateApplyResult>(`${BASE_URL}/${encodeURIComponent(key)}/apply`, options ?? {}).then((r) => r.data);

/** The saved wizard draft, or the template's defaults when nothing has been saved yet. */
export const getCurriculumTemplateDraft = (key: string): Promise<CurriculumTemplateDraft> =>
    http.get<CurriculumTemplateDraft>(`${BASE_URL}/${encodeURIComponent(key)}/draft`).then((r) => r.data);

/** Saves the wizard's progress; half-finished choices are kept, and the reply says what is still wrong. */
export const saveCurriculumTemplateDraft = (
    key: string,
    currentStep: string,
    options: CurriculumTemplateApplyOptions
): Promise<CurriculumTemplateDraft> =>
    http.put<CurriculumTemplateDraft>(`${BASE_URL}/${encodeURIComponent(key)}/draft`, { currentStep, options }).then((r) => r.data);

export const discardCurriculumTemplateDraft = (key: string): Promise<void> =>
    http.delete(`${BASE_URL}/${encodeURIComponent(key)}/draft`).then(() => undefined);
